import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Unified reCAPTCHA hook that supports the modes defined by the form backend:
 *  - off            : no-op, getSubmitProof() resolves to null
 *  - v2             : checkbox or invisible widget
 *  - v3             : execute only on submit
 *  - v3_v2_fallback : try v3 first; on RECAPTCHA_CHALLENGE_REQUIRED render v2
 *
 * Implementation uses the third-party React libraries:
 *  - v2: react-google-recaptcha (component ref -> executeAsync / getValue / reset)
 *  - v3: react-google-recaptcha-v3 (GoogleReCaptchaProvider + useGoogleReCaptcha)
 *
 * The v3 `executeRecaptcha` function is obtained from inside the Provider tree
 * via the <FormRecaptcha> component, which renders a small inner consumer that
 * calls `useGoogleReCaptcha()` and forwards the function back through onV3Ready.
 *
 * Returned handlers (passed to <FormRecaptcha>):
 *  - v2Ref        : ref for <ReCAPTCHA> component
 *  - onV3Ready    : called by the inner v3 consumer when executeRecaptcha is ready
 *  - onV3Error    : called when the v3 script fails to load
 *  - onV2Change   : token change callback (v2 checkbox / invisible)
 *  - onV2Expired  : token expired callback
 *  - onV2Errored  : widget error callback
 *
 * Public API (used by page submit handlers):
 *  - getSubmitProof() : Promise<{version, token} | null>
 *  - reset()          : reset the v2 widget
 *  - renderChallenge(cfg) : enter v2 fallback mode using backend-provided config
 *  - clearChallenge() : exit fallback mode and reset v2 widget
 */
export function useFormRecaptcha(recaptchaConfig) {
    const enabled = !!recaptchaConfig?.enabled;
    const mode = recaptchaConfig?.mode || "off";

    const [scriptReady, setScriptReady] = useState(false);
    const [scriptError, setScriptError] = useState(null);

    // v2 widget state
    const v2Ref = useRef(null);
    const [v2Token, setV2Token] = useState(null);
    const [v2Expired, setV2Expired] = useState(false);
    // Error that occurred AFTER user interaction (e.g. executeAsync failed,
    // verification returned invalid token). This is safe to surface in the UI.
    const [v2Error, setV2Error] = useState(false);
    // Error that occurred DURING widget initialization (before user interaction).
    // Typically means the Google site key is invalid / wrong type / domain not
    // whitelisted. We surface this as a blocking config error.
    const [v2InitError, setV2InitError] = useState(null);
    // Whether the user has at least attempted to execute the v2 widget once.
    // Used to decide whether to show v2Error (only show after first attempt).
    const [v2HasAttempted, setV2HasAttempted] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);

    // v3 execute function provided by <GoogleReCaptchaProvider>'s useGoogleReCaptcha().
    // Stored in a ref so getSubmitProof always reads the latest value without
    // needing it in its dependency array.
    const executeRecaptchaRef = useRef(null);
    const [, setExecuteRecaptchaVersion] = useState(0);

    // For v3_v2_fallback: the challenge returned by the backend.
    const [challenge, setChallenge] = useState(null);

    // Which v2 config to use: backend challenge (fallback) takes priority.
    const v2Config = useMemo(() => {
        if (challenge) return challenge;
        return recaptchaConfig?.v2 || null;
    }, [challenge, recaptchaConfig]);

    const v3Config = useMemo(() => recaptchaConfig?.v3 || null, [recaptchaConfig]);

    // Effective mode considering fallback challenge.
    const effectiveMode = useMemo(() => {
        if (challenge) return "v2";
        return mode;
    }, [challenge, mode]);

    // Whether the v2 widget should be rendered in the DOM.
    const showV2Widget = useMemo(() => {
        if (!enabled) return false;
        if (effectiveMode === "v2") return !!v2Config?.siteKey;
        return false;
    }, [enabled, effectiveMode, v2Config]);

    // Whether the v3 Provider should be rendered in the DOM.
    const showV3Widget = useMemo(() => {
        if (!enabled) return false;
        if (effectiveMode === "v3" || effectiveMode === "v3_v2_fallback") {
            return !!v3Config?.siteKey;
        }
        return false;
    }, [enabled, effectiveMode, v3Config]);

    // Clear v2 token state whenever the v2 widget is unmounted (e.g. when
    // entering fallback mode we switch from v3 -> v2; when leaving fallback
    // mode we switch back from v2 -> v3).
    useEffect(() => {
        if (!showV2Widget) {
            setV2Token(null);
            setV2Expired(false);
            setV2Error(false);
            setV2InitError(null);
            setV2HasAttempted(false);
        }
    }, [showV2Widget]);

    // Reset v3 state when the Provider is unmounted.
    useEffect(() => {
        if (!showV3Widget) {
            executeRecaptchaRef.current = null;
            setScriptReady(false);
        }
    }, [showV3Widget]);

    // ---- Callbacks forwarded to <FormRecaptcha> ----

    // Called by the inner v3 consumer once useGoogleReCaptcha resolves a ready
    // executeRecaptcha function. May fire multiple times (executeRecaptcha is
    // re-created by the library on each render) - we store the latest reference.
    const onV3Ready = useCallback((executeFn) => {
        if (typeof executeFn !== "function") return;
        executeRecaptchaRef.current = executeFn;
        setScriptReady(true);
        setScriptError(null);
        setExecuteRecaptchaVersion((v) => v + 1);
    }, []);

    const onV3Error = useCallback((err) => {
        setScriptError(err?.message || "reCAPTCHA failed to load");
    }, []);

    const onV2Ready = useCallback(() => {
        // Only flip scriptReady to true when we're not relying on the v3
        // provider (otherwise the v3 Provider drives scriptReady).
        if (!showV3Widget) {
            setScriptReady(true);
        }
    }, [showV3Widget]);

    const onV2Change = useCallback((token) => {
        setV2Token(token);
        setV2Expired(false);
        setV2Error(false);
    }, []);

    const onV2Expired = useCallback(() => {
        setV2Token(null);
        setV2Expired(true);
    }, []);

    const onV2Errored = useCallback(() => {
        // When Google rejects the widget during initialization (bad site key,
        // wrong type, domain not whitelisted), error-callback fires before any
        // user interaction. We treat this as a config-level init error so the
        // UI can surface it clearly and block the submit button.
        //
        // When error-callback fires AFTER executeAsync (verification failed,
        // network issue), we set v2Error normally so the error shows to the user.
        console.error("[reCAPTCHA v2] onErrored fired. v2HasAttempted=", v2HasAttempted, "isExecuting=", isExecuting);
        setV2Token(null);
        if (!v2HasAttempted && !isExecuting) {
            // Init-time config error (before user tried anything)
            setV2InitError(
                "reCAPTCHA widget failed to load. Please check the site key configuration."
            );
            // Also set as scriptError so the Submit button blocks on it.
            setScriptError(
                "reCAPTCHA init error: site key may be invalid or misconfigured."
            );
        } else {
            // Post-execution verification error
            setV2Error(true);
        }
    }, [v2HasAttempted, isExecuting]);

    // ---- Public API ----

    const reset = useCallback(() => {
        if (v2Ref.current) {
            try {
                v2Ref.current.reset();
            } catch (e) {
                // ignore
            }
        }
        setV2Token(null);
        setV2Expired(false);
        setV2Error(false);
        setV2InitError(null);
    }, []);

    const renderChallenge = useCallback((challengeConfig) => {
        setChallenge(challengeConfig || null);
    }, []);

    const clearChallenge = useCallback(() => {
        setChallenge(null);
        if (v2Ref.current) {
            try {
                v2Ref.current.reset();
            } catch (e) {
                // ignore
            }
        }
        setV2Token(null);
        setV2Expired(false);
        setV2Error(false);
        setV2InitError(null);
    }, []);

    /**
     * Obtain the recaptcha proof to attach to the submit body.
     *
     * Resolves to:
     *  - null when recaptcha is disabled / off
     *  - { version: 'v3', token } for v3 mode
     *  - { version: 'v2', token } for v2 mode
     */
    const getSubmitProof = useCallback(async () => {
        if (!enabled || effectiveMode === "off") {
            return null;
        }
        if (scriptError) {
            throw new Error("reCAPTCHA failed to load, please refresh the page");
        }
        if (v2InitError) {
            throw new Error(v2InitError);
        }

        if (effectiveMode === "v2") {
            const isInvisible = v2Config?.widget === "invisible";
            if (isInvisible) {
                if (!v2Ref.current) {
                    throw new Error("reCAPTCHA v2 widget is not ready");
                }
                setV2HasAttempted(true);
                setIsExecuting(true);
                try {
                    const token = await v2Ref.current.executeAsync();
                    if (!token) {
                        throw new Error("reCAPTCHA verification failed");
                    }
                    setV2Token(token);
                    setV2Error(false);
                    return { version: "v2", token };
                } finally {
                    setIsExecuting(false);
                }
            }
            // checkbox
            if (v2Token) {
                return { version: "v2", token: v2Token };
            }
            if (v2Error) {
                throw new Error("reCAPTCHA verification failed, please retry");
            }
            throw new Error("Please complete the reCAPTCHA verification");
        }

        // v3 (pure v3 or v3_v2_fallback's first attempt)
        if (!v3Config?.siteKey) {
            throw new Error("reCAPTCHA v3 site key is missing");
        }
        const execute = executeRecaptchaRef.current;
        if (typeof execute !== "function") {
            throw new Error("reCAPTCHA v3 is not ready, please retry");
        }
        setIsExecuting(true);
        try {
            const action = v3Config.action || "form_submit";
            const token = await execute(action);
            return { version: "v3", token };
        } finally {
            setIsExecuting(false);
        }
    }, [enabled, effectiveMode, scriptError, v2Config, v2Token, v2Error, v3Config]);

    return {
        // Refs / handlers forwarded to <FormRecaptcha>
        v2Ref,
        v2Config,
        v3Config,
        onV3Ready,
        onV3Error,
        onV2Ready,
        onV2Change,
        onV2Expired,
        onV2Errored,
        // Public API
        getSubmitProof,
        reset,
        renderChallenge,
        clearChallenge,
        // State for <FormRecaptcha> rendering
        scriptReady,
        scriptError,
        isExecuting,
        enabled,
        mode,
        showV2Widget,
        showV3Widget,
        v2TokenReady: !!v2Token,
        v2Expired,
        // Only show verification error AFTER user has attempted submit, so
        // we don't leak init-time config errors as "verification failed".
        v2Error: v2Error && v2HasAttempted,
        // Init-time config error (bad site key, domain not whitelisted, etc.)
        v2InitError,
    };
}
